import type { BoardItem, FollowState } from './apiClient';
import { SESSION_EXPIRED_MESSAGE, isNotFoundError, statusOf } from './httpErrors';

/**
 * The rules behind the "Everyone's feedback" tab and the suggestions, kept out of the components so each is testable on its own.
 * The API decides what is allowed (only an open, public item can be followed, and the sender cannot undo following their own); these
 * mirror it only so the screen offers what will work, and the API's answer is still what happens.
 */

/** The board sends at most this many items (`BOARD_LIMIT` in the API) and no total, so a list of exactly this many may have been cut short. */
export const BOARD_LIMIT = 200;

/** ⚠️ At the cap the list MAY be cut short, and which end it is cut at matters: open requests come first, so it is older CLOSED items that are missing. */
export const boardMayBeCut = (listed: number): boolean => listed >= BOARD_LIMIT;

/**
 * The start of a message on one line, for a suggestion: whitespace collapsed across ALL its lines, then cut to `max` characters with an
 * ellipsis. Not `firstLine`: a message that opens "Hi team," would show only that, and the person could not tell why it was suggested.
 */
export function snippet(message: string, max = 160): string {
  const chars = Array.from(message.replace(/\s+/g, ' ').trim());
  return chars.length > max ? `${chars.slice(0, max - 1).join('').trimEnd()}…` : chars.join('');
}

/** What the control on an item shows. `sent` and `following` come first: a person's own relation to an item beats whether it is open. */
export type MeTooState = 'sent' | 'following' | 'can' | 'closed';

/**
 * - `sent`: I sent it. There is nothing to add and nothing to undo: the sender always follows their own item.
 * - `following`: I said "me too" (or follow it some other way), open or closed. I can undo it.
 * - `can`: it is open and I do not follow it: "Me too".
 * - `closed`: it is closed and I do not follow it. Following would add somebody who will never hear anything, and the API refuses it.
 */
export const meTooState = (i: Pick<BoardItem, 'sentByMe' | 'following' | 'status'>): MeTooState =>
  i.sentByMe ? 'sent' : i.following ? 'following' : i.status === 'open' ? 'can' : 'closed';

/** "N people want this": what the count of followers (the sender included) tells the team. Empty for none, which is not a thing to say aloud. */
export const wantsLabel = (n: number): string => (n <= 0 ? '' : n === 1 ? '1 person wants this' : `${n} people want this`);

/**
 * The board after somebody followed or unfollowed one item, from the API's own answer (whether they follow it now, and the new
 * count). Every other item is returned as it was, the same object, so nothing about them is copied or changed.
 */
export function applyFollowState(items: readonly BoardItem[] | undefined, id: number, state: FollowState): BoardItem[] | undefined {
  if (!items) return undefined;
  return items.map((i) => {
    if (i.id !== id) return i;
    const next: BoardItem = { ...i, following: state.following, followerCount: state.followerCount };
    // A follow that is gone was not "added by a merge" any more: otherwise saying "me too" again would be worded as if an admin had done it.
    if (!state.following && i.addedByMerge) next.addedByMerge = false;
    return next;
  });
}

/** The refusals that carry words written for a person ("This item is closed"): a 400, 403, 409 or 422. The rest are the machinery's. */
const SPEAKS_FOR_ITSELF = new Set([400, 403, 409, 422]);
/** What the framework says when nobody wrote anything: true of the status, and no help to the person. */
const FRAMEWORK_WORDS = /^(bad request|forbidden( resource)?|conflict|unprocessable entity)$/i;

/**
 * What to tell somebody whose follow or undo did not go through. The API's own words when it REFUSED with some written for them (a 400,
 * 403, 409 or 422), because they say why ("This item is closed"). Not for a 404, which the API answers with the item's internal number
 * ("Feedback 12 not found") and which means, to the person, that an admin took the item off the board while they were reading it. And
 * not for anything else: a 401 ("Unauthorized"), a 429 ("ThrottlerException: Too Many Requests"), a 5xx, a network fault or no answer
 * at all come with the machinery's words ("Network Error", "Request failed with status code 502"), which tell nobody anything.
 */
export function followFailure(error: unknown): string {
  if (isNotFoundError(error)) return 'That request isn’t on the board any more.';
  const status = statusOf(error);
  const message = (error as { message?: unknown } | null)?.message;
  const said = typeof message === 'string' ? message.trim() : '';
  if (status !== undefined && SPEAKS_FOR_ITSELF.has(status) && said && !FRAMEWORK_WORDS.test(said)) return said;
  if (said === SESSION_EXPIRED_MESSAGE) return said; // no status, but it says what to do, and "try again" can never work: only a refresh does
  return 'That did not go through. Try again in a moment.';
}

/** A copy of `set` with `id` in it. State must not be changed in place, or React would not see it change. */
export const withId = (set: ReadonlySet<number>, id: number): Set<number> => new Set(set).add(id);

/** A copy of `set` without `id`. */
export const withoutId = (set: ReadonlySet<number>, id: number): Set<number> => {
  const next = new Set(set);
  next.delete(id);
  return next;
};
